/**
 * Practical challenge engine — browser copy of
 * backend/src/utils/challengeEngine.js so the workbook can show progress,
 * scores and missing answers live. Keep the two files in sync; the server
 * re-checks everything on save and submit.
 */

const INPUT_TYPES = [
    'short_text', 'long_text', 'number', 'date', 'checkbox',
    'score', 'single_choice', 'checklist', 'process_steps', 'file_upload'
];
const DISPLAY_TYPES = ['heading', 'info'];
const FIELD_TYPES = [...INPUT_TYPES, ...DISPLAY_TYPES];

const MAX_TEXT = 10000;
const MAX_STEPS = 30;

const READINESS_LEVELS = [
    { key: 'low', label: 'Low' },
    { key: 'developing', label: 'Developing' },
    { key: 'good', label: 'Good' },
    { key: 'strong', label: 'Strong' }
];

function isInputField(field) {
    return field && INPUT_TYPES.includes(field.type);
}

function allFields(challenge) {
    const out = [];
    for (const section of (challenge && challenge.sections) || []) {
        for (const field of section.fields || []) out.push({ section, field });
    }
    return out;
}

function text(value) {
    return typeof value === 'string' ? value.trim() : '';
}

/** A field whose `depends_on` checkbox is not ticked is hidden and never required. */
function isFieldVisible(field, answers) {
    if (!field.depends_on) return true;
    const dep = answers && answers[field.depends_on];
    return Boolean(dep && dep.value === true);
}

function filesForField(files, fieldKey) {
    return (files || []).filter((f) => f.field_key === fieldKey);
}

/** True when the field holds a usable answer (or a valid N/A). */
function isFieldComplete(field, answer, files) {
    if (!isInputField(field)) return true;
    const a = answer || {};

    if (field.allow_na && a.na) {
        // A score marked N/A still needs a short reason (workbook rule).
        return field.type === 'score' ? Boolean(text(a.reason)) : true;
    }

    switch (field.type) {
        case 'short_text':
        case 'long_text':
        case 'date':
            return Boolean(text(a.value));
        case 'number': {
            if (a.value === '' || a.value === null || a.value === undefined) return false;
            const n = Number(a.value);
            if (!Number.isFinite(n)) return false;
            if (field.min !== undefined && field.min !== null && field.min !== '' && n < Number(field.min)) return false;
            if (field.max !== undefined && field.max !== null && field.max !== '' && n > Number(field.max)) return false;
            return true;
        }
        case 'checkbox':
            return a.value === true;
        case 'score':
            return Number.isInteger(a.score) && a.score >= 1 && a.score <= 5 && Boolean(text(a.reason));
        case 'single_choice':
            return (field.options || []).some((o) => o.value === a.value);
        case 'checklist': {
            const items = field.items || [];
            const checked = new Set(Array.isArray(a.checked) ? a.checked : []);
            return items.length > 0 && items.every((_, i) => checked.has(i));
        }
        case 'process_steps': {
            const steps = (Array.isArray(a.steps) ? a.steps : []).filter((s) => text(s));
            return steps.length > 0 && Boolean(text(a.outcome));
        }
        case 'file_upload': {
            const list = filesForField(files, field.key);
            return list.length > 0;
        }
        default:
            return true;
    }
}

/** True when the field has any content at all (used for optional-only sections). */
function hasContent(field, answer, files) {
    if (!isInputField(field)) return false;
    const a = answer || {};
    if (a.na) return true;
    switch (field.type) {
        case 'checkbox': return a.value === true;
        case 'score': return Number.isInteger(a.score);
        case 'checklist': return Array.isArray(a.checked) && a.checked.length > 0;
        case 'process_steps': return (a.steps || []).some((s) => text(s)) || Boolean(text(a.outcome));
        case 'file_upload': return filesForField(files, field.key).length > 0;
        case 'number': return a.value !== '' && a.value !== null && a.value !== undefined;
        default: return Boolean(text(a.value));
    }
}

/** Uploaded files missing their consent tick block submission even when optional. */
function missingConsent(field, files) {
    if (field.type !== 'file_upload' || !field.consent_label) return false;
    return filesForField(files, field.key).some((f) => !f.consent);
}

/** "Part B" for workbook parts, "Step 2" for sections that carry their own eyebrow. */
function sectionLabel(section) {
    if (section.eyebrow) return String(section.eyebrow).split('·')[0].trim();
    return `Part ${section.key}`;
}

function sectionStatus(section, answers, files) {
    const visible = (section.fields || []).filter((f) => isInputField(f) && isFieldVisible(f, answers));
    const required = visible.filter((f) => f.required);
    const missing = required.filter((f) => !isFieldComplete(f, answers[f.key], files));
    const consentIssues = visible.filter((f) => missingConsent(f, files));
    const answered = visible.filter((f) => hasContent(f, answers[f.key], files)).length;
    const naCount = visible.filter((f) => f.allow_na && answers[f.key] && answers[f.key].na).length;

    let done;
    if (required.length > 0) done = missing.length === 0 && consentIssues.length === 0;
    else done = answered > 0 && consentIssues.length === 0;

    return {
        key: section.key,
        title: section.title,
        label: sectionLabel(section),
        done,
        required: required.length,
        missing: missing.map((f) => f.key),
        consent_missing: consentIssues.map((f) => f.key),
        answered,
        na_count: naCount
    };
}

function computeScore(challenge, answers) {
    const rows = [];
    let total = 0;
    let max = 0;
    let naCount = 0;
    for (const { field } of allFields(challenge)) {
        if (field.type !== 'score') continue;
        const a = (answers && answers[field.key]) || {};
        if (field.allow_na && a.na) {
            naCount += 1;
            rows.push({ key: field.key, label: field.label, short_label: field.short_label || field.label, na: true, score: null });
            continue;
        }
        max += 5;
        const score = Number.isInteger(a.score) ? a.score : null;
        if (score) total += score;
        rows.push({ key: field.key, label: field.label, short_label: field.short_label || field.label, na: false, score });
    }
    if (rows.length === 0) return null;
    const percent = max > 0 ? Math.round((total / max) * 100) : 0;
    return {
        total,
        max,
        percent,
        na_count: naCount,
        scored_count: rows.length - naCount,
        level: readinessLevel(total, max),
        rows
    };
}

/**
 * Bands from the workbook: 6–12 low, 13–20 developing, 21–25 good, 26–30 strong.
 * When areas are N/A the same bands apply to the share of the possible score.
 */
function readinessLevel(total, max) {
    if (!max) return null;
    const scaled = total * 30;
    if (scaled <= 12 * max) return 'low';
    if (scaled <= 20 * max) return 'developing';
    if (scaled <= 25 * max) return 'good';
    return 'strong';
}

function computeProgress(challenge, answers, files) {
    const sections = (challenge.sections || []).filter((s) => (s.fields || []).some(isInputField));
    const statuses = sections.map((s) => sectionStatus(s, answers || {}, files));
    const doneCount = statuses.filter((s) => s.done).length;

    let percent;
    if (challenge.layout === 'workbook') {
        percent = statuses.length ? Math.round((doneCount / statuses.length) * 100) : 0;
    } else {
        const required = statuses.reduce((n, s) => n + s.required, 0);
        const missing = statuses.reduce((n, s) => n + s.missing.length, 0);
        percent = required ? Math.round(((required - missing) / required) * 100) : (doneCount ? 100 : 0);
    }
    return { sections: statuses, done_count: doneCount, total_sections: statuses.length, percent };
}

/** Everything that stops a submission, as section-level messages. */
function validateForSubmit(challenge, answers, files) {
    const progress = computeProgress(challenge, answers || {}, files);
    const problems = [];
    for (const s of progress.sections) {
        if (s.missing.length) {
            problems.push({
                section_key: s.key,
                field_keys: s.missing,
                message: `${s.label} ${s.title.charAt(0).toLowerCase()}${s.title.slice(1)} (${s.missing.length} ${s.missing.length === 1 ? 'answer' : 'answers'})`
            });
        }
        if (s.consent_missing.length) {
            problems.push({
                section_key: s.key,
                field_keys: s.consent_missing,
                message: `${s.label} — tick the permission box for every uploaded file`
            });
        }
    }
    return { ok: problems.length === 0, problems, progress };
}

function clampText(value) {
    if (typeof value !== 'string') return '';
    return value.slice(0, MAX_TEXT);
}

/** Keep only known fields, coerced to the shape their type expects. */
function sanitizeAnswers(challenge, raw) {
    const input = raw && typeof raw === 'object' ? raw : {};
    const out = {};
    for (const { field } of allFields(challenge)) {
        if (!isInputField(field) || field.type === 'file_upload') continue;
        const a = input[field.key];
        if (!a || typeof a !== 'object') continue;
        const na = Boolean(field.allow_na && a.na);
        switch (field.type) {
            case 'short_text':
            case 'long_text':
            case 'date':
                out[field.key] = { value: clampText(a.value), na };
                break;
            case 'number': {
                const v = a.value === '' || a.value === null || a.value === undefined ? '' : Number(a.value);
                out[field.key] = { value: Number.isFinite(v) ? v : '', na };
                break;
            }
            case 'checkbox':
                out[field.key] = { value: a.value === true };
                break;
            case 'score': {
                const s = Number(a.score);
                out[field.key] = {
                    score: !na && Number.isInteger(s) && s >= 1 && s <= 5 ? s : null,
                    reason: clampText(a.reason),
                    na
                };
                break;
            }
            case 'single_choice': {
                const valid = (field.options || []).some((o) => o.value === a.value);
                out[field.key] = { value: valid ? a.value : null, na };
                break;
            }
            case 'checklist': {
                const n = (field.items || []).length;
                const checked = [...new Set((Array.isArray(a.checked) ? a.checked : [])
                    .map(Number).filter((i) => Number.isInteger(i) && i >= 0 && i < n))].sort((x, y) => x - y);
                out[field.key] = { checked };
                break;
            }
            case 'process_steps': {
                const steps = (Array.isArray(a.steps) ? a.steps : [])
                    .slice(0, MAX_STEPS).map((s) => (typeof s === 'string' ? s.slice(0, 200) : ''));
                out[field.key] = { steps, outcome: clampText(a.outcome).slice(0, 500), na };
                break;
            }
            default:
                break;
        }
    }
    return out;
}

/** Validate an admin-authored challenge definition. Returns a list of errors. */
function validateDefinition(def) {
    const errors = [];
    if (!def || typeof def !== 'object') return ['Challenge definition is missing'];
    if (!text(def.title)) errors.push('Title is required');
    if (!['single', 'workbook'].includes(def.layout)) errors.push('Layout must be single or workbook');
    if (!Array.isArray(def.sections) || def.sections.length === 0) errors.push('Add at least one section');
    const sectionKeys = new Set();
    const fieldKeys = new Set();
    for (const section of def.sections || []) {
        if (!text(section.key)) errors.push('Every section needs a key');
        else if (sectionKeys.has(section.key)) errors.push(`Duplicate section key "${section.key}"`);
        sectionKeys.add(section.key);
        if (!text(section.title)) errors.push(`Section ${section.key || '?'} needs a title`);
        for (const field of section.fields || []) {
            if (!FIELD_TYPES.includes(field.type)) errors.push(`Unknown field type "${field.type}"`);
            if (!isInputField(field)) continue;
            if (!text(field.key)) errors.push(`A field in section ${section.key} has no key`);
            else if (fieldKeys.has(field.key)) errors.push(`Duplicate field key "${field.key}"`);
            fieldKeys.add(field.key);
            if (!text(field.label)) errors.push(`A field in section ${section.key} has no label`);
            if (field.type === 'single_choice' && !(field.options || []).length) {
                errors.push(`"${field.label}" needs at least one option`);
            }
            if (field.type === 'checklist' && !(field.items || []).length) {
                errors.push(`"${field.label}" needs at least one checklist item`);
            }
        }
    }
    for (const { field } of allFields(def)) {
        if (field.depends_on && !fieldKeys.has(field.depends_on)) {
            errors.push(`"${field.label}" depends on a field that does not exist`);
        }
    }
    return errors;
}

export {
    INPUT_TYPES,
    DISPLAY_TYPES,
    FIELD_TYPES,
    READINESS_LEVELS,
    isInputField,
    allFields,
    isFieldVisible,
    sectionLabel,
    isFieldComplete,
    hasContent,
    sectionStatus,
    computeScore,
    readinessLevel,
    computeProgress,
    validateForSubmit,
    sanitizeAnswers,
    validateDefinition
};
